// redirect-amend-option-inference.ts — phase 06i R1 (dogfood 8, F-45; debate B6).
//
// A redirect AMEND of ticket X often IS the operator's answer to X's pending
// `needs_decision` question (D8: "QM-1 chốt = raise ValueError…" in
// `patch.outcome`). Reading that answer back from free text is only safe when
// the operator names exactly ONE option id and marks it as an id: wrapped in
// backticks/quotes, or right after "option id". Labels are never matched (Q2),
// bare ids are not either (a sentence can name an id to reject it), and ids
// shorter than 3 chars or all digits make the question `not_inferable` ("rev 2"
// would read as option "2"). Pure: no I/O, no journal.
import type { RedirectDirectivePayload } from "./directive-redirect-application.js";
import { OPTION_ID_PATTERN } from "./needs-decision-option-id-label-pairs.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

export type AmendOptionInferenceFailure =
  | "no_option_id_in_patch"
  | "ambiguous_option_ids"
  | "not_inferable";

export type AmendOptionInference =
  | { ok: true; optionId: string }
  | { ok: false; reason: AmendOptionInferenceFailure };

const MIN_INFERABLE_ID_LENGTH = 3;

function acceptanceText(element: unknown): string | undefined {
  if (typeof element === "string") return element;
  if (element !== null && typeof element === "object" && "text" in element) {
    const text = (element as { text: unknown }).text;
    return typeof text === "string" ? text : undefined;
  }
  return undefined;
}

/** Text of every `amend` of `ticketId`: `patch.outcome`, `patch.title`, `patch.acceptance[]`; `undefined` = no amend. */
function amendPatchText(payload: RedirectDirectivePayload, ticketId: string): string | undefined {
  const parts: string[] = [];
  let amended = false;
  for (const change of payload.proposed_changes ?? []) {
    if (change.op !== "amend" || change.ticket_id !== ticketId) continue;
    amended = true;
    const patch = change.patch ?? {};
    if (typeof patch.outcome === "string") parts.push(patch.outcome);
    if (typeof patch.title === "string") parts.push(patch.title);
    for (const element of patch.acceptance ?? []) {
      const text = acceptanceText(element);
      if (text !== undefined) parts.push(text);
    }
  }
  return amended ? parts.join("\n") : undefined;
}

/** Review 06i L3: text is lower-cased, so an id outside OPTION_ID_PATTERN could never match — not inferable. */
function idIsInferable(id: string): boolean {
  return OPTION_ID_PATTERN.test(id) && id.length >= MIN_INFERABLE_ID_LENGTH && !/^\d+$/.test(id);
}

/** `\`id\``, `"id"`, `'id'`, or `option id[:] id` as a whole token (B6). */
function namesOptionId(text: string, id: string): boolean {
  const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const wrapped = `\`${escaped}\`|"${escaped}"|'${escaped}'`;
  const phrase = `option[ _-]?id[:\\s]*(?<![a-z0-9-])${escaped}(?![a-z0-9-])`;
  return new RegExp(`${wrapped}|${phrase}`).test(text);
}

export function inferAmendOptionId(
  payload: RedirectDirectivePayload,
  ticketId: string,
  question: InterviewQuestion,
): AmendOptionInference {
  const ids = question.option_ids;
  if (ids === undefined || !ids.every(idIsInferable)) return { ok: false, reason: "not_inferable" };
  const text = amendPatchText(payload, ticketId);
  if (text === undefined) return { ok: false, reason: "not_inferable" };
  const normalized = text.normalize("NFC").toLowerCase();
  const named = ids.filter((id) => namesOptionId(normalized, id));
  if (named.length === 1) return { ok: true, optionId: named[0] as string };
  return {
    ok: false,
    reason: named.length === 0 ? "no_option_id_in_patch" : "ambiguous_option_ids",
  };
}
